import { getRequestConfig, withMakeRequest } from '~/api';
import Request from '~/api/Request.ts';
import { PasskeyCredential } from '~/api/types/passkeysData.ts';

type ListPasskeyCredentialsResponse = {
  credentials: PasskeyCredential[];
  allAcceptedCredentialsOptions?: {
    rpId: string;
    userId: string;
    allAcceptedCredentialIds: string[];
  };
};

const listPasskeyCredentials: (
  userId?: string
) => Promise<ListPasskeyCredentialsResponse> = withMakeRequest(
  async (userId?: string) =>
    await Request.getInstance().get(
      `/webauthn/credentials${userId ? `?user_id=${userId}` : ''}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: ListPasskeyCredentialsResponse) => data
);

type UpdatePasskeyCredentialResponse = {
  credential: PasskeyCredential;
};

const updatePasskeyCredential: (request: {
  id: string;
  displayName: string;
}) => Promise<PasskeyCredential> = withMakeRequest(
  async ({ id, displayName }: { id: string; displayName: string }) =>
    await Request.getInstance().patch(
      `/webauthn/credentials/${id}`,
      {
        displayName
      },
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UpdatePasskeyCredentialResponse) => data.credential
);

export type DeletePasskeyCredentialResponse = {
  success: boolean;
  unknownCredentialOptions: {
    rpId: string;
    credentialId: string;
  };
};

const deletePasskeyCredential: (request: {
  id: string;
}) => Promise<DeletePasskeyCredentialResponse> = withMakeRequest(
  async ({ id }: { id: string }) =>
    await Request.getInstance().delete(
      `/webauthn/credentials/${id}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: DeletePasskeyCredentialResponse) => data
);

export type StartPasskeyRegistrationResponse = {
  options: Record<string, any>;
};

const startPasskeyRegistration: () => Promise<StartPasskeyRegistrationResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().post(
        `/webauthn/registration:begin`,
        {},
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: StartPasskeyRegistrationResponse) => data
  );

export type FinishPasskeyRegistrationRequest = {
  type: string;
  credentialId: string;
  transports: string[];
  clientExtensionResults: Record<string, any>;
  authenticatorAttachment?: string;
  clientDataJSON: string;
  attestationObject: string;
};

export type FinishPasskeyRegistrationResponse = {
  success: boolean;
};

const finishPasskeyRegistration: (
  request: FinishPasskeyRegistrationRequest
) => Promise<FinishPasskeyRegistrationResponse> = withMakeRequest(
  async (request: FinishPasskeyRegistrationRequest) =>
    await Request.getInstance().post(
      `/webauthn/registration:finish`,
      request,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: FinishPasskeyRegistrationResponse) => data
);

type StartPasskeyAuthenticationRequest = { email?: string };

export type StartPasskeyAuthenticationResponse = {
  sessionId: string;
  options: Record<string, any>;
};

const startPasskeyAuthentication: (
  request: StartPasskeyAuthenticationRequest
) => Promise<StartPasskeyAuthenticationResponse> = withMakeRequest(
  async (request: StartPasskeyAuthenticationRequest) =>
    await Request.getInstance().post(
      `/webauthn/authentication:begin`,
      request,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: StartPasskeyAuthenticationResponse) => data
);

export type FinishPasskeyAuthenticationRequest = {
  type: string;
  sessionId: string;
  credentialId: string;
  clientExtensionResults: Record<string, any>;
  authenticatorAttachment: string | null;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
};

export type FinishPasskeyAuthenticationResponse = {
  success: boolean;
  userId: string;
  sessionToken: string;
};

const finishPasskeyAuthentication: (
  request: FinishPasskeyAuthenticationRequest
) => Promise<FinishPasskeyAuthenticationResponse> = withMakeRequest(
  async (request: FinishPasskeyAuthenticationRequest) =>
    await Request.getInstance().post(
      `/webauthn/authentication:finish`,
      request,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: FinishPasskeyAuthenticationResponse) => data
);

export {
  startPasskeyRegistration,
  finishPasskeyRegistration,
  startPasskeyAuthentication,
  finishPasskeyAuthentication,
  listPasskeyCredentials,
  updatePasskeyCredential,
  deletePasskeyCredential
};
